import { motion } from "framer-motion";
import Reveal from "./Reveal";
import { useLang } from "../i18n";

const milestoneDefs = [
  {
    tk: "time.m1t",
    hk: "time.m1h",
    bk: "time.m1b",
    t: "Now",
    title: "Subliminal-Scale Research",
    body: "Deep research and large-scale computing programs are running now, quietly, below the radar, across physics, AI, and biology.",
  },
  {
    tk: "time.m2t",
    hk: "time.m2h",
    bk: "time.m2b",
    t: "T + 12 Months",
    title: "First Candidate Interventions",
    body: "The earliest validated outputs of the program move from simulation into structured evaluation.",
  },
  {
    tk: "time.m3t",
    hk: "time.m3h",
    bk: "time.m3b",
    t: "T + 24 Months",
    title: "First Solutions Roll Out",
    body: "Our first solutions ship. The beginning of a compounding pipeline toward radical lifespan extension.",
  },
];

const Timeline = () => {
  const { t } = useLang();
  return (
    <section id="horizon" data-testid="timeline-section" className="relative overflow-hidden py-28 md:py-40">
      <div className="pointer-events-none absolute inset-0 opacity-40">
        <img
          src="https://images.pexels.com/photos/8533018/pexels-photo-8533018.jpeg?auto=compress&cs=tinysrgb&w=1600"
          alt=""
          loading="lazy"
          className="h-full w-full object-cover brightness-[0.2] contrast-125 grayscale"
        />
        <div className="media-veil absolute inset-0 bg-gradient-to-b from-void via-void/80 to-void" />
      </div>

      <div className="relative mx-auto max-w-6xl px-6">
        <Reveal testId="timeline-heading" className="text-center">
          <p className="text-gradient mb-6 font-mono text-base uppercase tracking-[0.3em]">
            {t("time.eyebrow", "The Horizon")}
          </p>
          <h2 className="mx-auto max-w-3xl font-display text-[2rem] font-semibold leading-[1.08] tracking-tight sm:text-[2.75rem]">
            {t("time.h2a", "Two years to")} <span className="text-gradient">{t("time.h2b", "first solutions.")}</span>
          </h2>
        </Reveal>

        <div className="mx-auto mt-16 max-w-2xl">
          {milestoneDefs.map((m, i) => (
            <motion.div
              key={m.t}
              data-testid={`timeline-node-${i + 1}`}
              initial={{ opacity: 0, x: -32 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="group relative border-l border-white/15 pb-16 pl-12 last:pb-0"
            >
              <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-gradient-to-br from-violet-400 to-cyan-300 shadow-[0_0_14px_rgba(34,211,238,0.7)] transition-transform duration-500 group-hover:scale-150" />
              <p className="font-mono text-base uppercase tracking-[0.3em] text-cyan-300">{t(m.tk, m.t)}</p>
              <h3 className="mt-3 font-display text-2xl font-semibold tracking-tight md:text-3xl">
                {t(m.hk, m.title)}
              </h3>
              <p className="mt-3 text-base font-light leading-relaxed text-fog">
                {t(m.bk, m.body)}
              </p>
            </motion.div>
          ))}
        </div>

        <Reveal testId="timeline-stay-tuned" className="mt-20 text-center">
          <p className="text-gradient font-mono text-base uppercase tracking-[0.3em]">
            {t("time.outro", "Stay tuned. The clock is already running.")}
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default Timeline;
